import React, { useEffect, useState } from 'react';
import { RateInput } from 'components/template/TemplateForm';
import { useFormContext } from 'react-hook-form';
import { fetchSingleTemplate, fetchTemplates, templatesSelector } from 'slices/templateSlice';
import { useDispatch, useSelector } from 'react-redux';
import { AsyncPaginate } from 'react-select-async-paginate';
import { selectCustomStyles } from 'components/common/Select';
import { SubmitButton } from 'components/common/buttons/SubmitButton';
import { IconButton } from "@mui/material";
import toast from 'react-hot-toast';
import { Controller } from "react-hook-form";
import ErrorMessage from 'components/common/ErrorMessage';

const TranslationTiersPreview = ({ item }) => {
  const dispatch = useDispatch();
  const { templates, singleTemplate: fetchedTemplate } = useSelector(templatesSelector);
  const { setValue, control } = useFormContext();
  const [translationTemplateBlocks, setTranslationTemplateBlocks] = useState([
    { id: 0, filterTemplate: null, rush_fee: 0, rates: [] },
  ]);
  const [pendingBlockIndex, setPendingBlockIndex] = useState(null);

  const getSelectedTemplateIds = (excludeBlockIndex) =>
  translationTemplateBlocks
    .filter((_, idx) => idx !== excludeBlockIndex)
    .map((block) => block.filterTemplate?.value)
    .filter(Boolean);

  const normalizeRates = (rates = []) =>
    (Array.isArray(rates) ? rates : []).map((rate, index) => ({
      tier_id: rate?.tier_id ?? rate?.tier?.id ?? rate?.id ?? index + 1,
      tier_name: rate?.tier_name || rate?.tier?.name || rate?.name || '',
      translation_rate: rate?.translation_rate ?? 0,
      editing_rate: rate?.editing_rate ?? 0,
      dtp_rate: rate?.dtp_rate ?? 0,
      review_rate: rate?.review_rate ?? 0,
      audio_recording_rate: rate?.audio_recording_rate ?? 0,
      transcription_rate: rate?.transcription_rate ?? 0,
    }));

  const applyTemplateSelection = (blockIndex, selectedTemplate, rates = [], rushFee = 0) => {
    const normalizedRates = normalizeRates(rates);
    const templateId = selectedTemplate?.value || '';

    setTranslationTemplateBlocks((prev) => {
      const next = [...prev];
      next[blockIndex] = {
        ...next[blockIndex],
        filterTemplate: selectedTemplate,
        rates: normalizedRates,
        rush_fee: rushFee,
      };
      return next;
    });

    setValue(`translationTemplateBlocks.${blockIndex}.template_id`, templateId);
    setValue(`translationTemplateBlocks.${blockIndex}.filterTemplate`, selectedTemplate,{shouldValidate: true,});
    setValue(`translationTemplateBlocks.${blockIndex}.rush_fee`, rushFee);
    setValue(`translationTemplateBlocks.${blockIndex}.rates`, normalizedRates);
    setValue('translation_template_id', templateId);
    setValue('selected_translation_rates', normalizedRates);
  };

  const clearTemplateSelection = (blockIndex) => {
    setTranslationTemplateBlocks((prev) => {
      const next = [...prev];
      next[blockIndex] = { ...next[blockIndex], filterTemplate: null, rates: [] };
      return next;
    });

    setValue(`translationTemplateBlocks.${blockIndex}.template_id`, '');
    setValue(`translationTemplateBlocks.${blockIndex}.filterTemplate`, null, {shouldValidate: true,});
    setValue(`translationTemplateBlocks.${blockIndex}.rates`, []);
    setValue(`translationTemplateBlocks.${blockIndex}.rush_fee`, 0);
  };

  useEffect(() => {
    const rates = item?.translation_rates || [];
    if (!item?.id && !rates.length) {
      return;
    }

    const existingTemplateId =
      rates?.[0]?.client_template_id ||
      item?.translation_template_id ||
      item?.translation_template?.id ||
      null;

    const existingTemplateName =
      rates?.[0]?.client_template?.display_name ||
      rates?.[0]?.client_template?.name ||
      item?.translation_template?.display_name ||
      item?.translation_template?.name ||
      '';

    const existingRates = normalizeRates(
      rates.map((r) => ({
        tier_id: r.client_tier_id,
        tier_name: r.tier?.name,
        translation_rate: r.translation_rate,
        editing_rate: r.editing_rate,
        dtp_rate: r.dtp_rate,
        review_rate: r.review_rate,
        audio_recording_rate: r.audio_recording_rate,
        transcription_rate: r.transcription_rate,
      }))
    );

    if (existingTemplateId || existingRates.length) {
      const templateObj = existingTemplateId
        ? { value: existingTemplateId, label: existingTemplateName || `Template ${existingTemplateId}` }
        : null;

      setTranslationTemplateBlocks([{ id: 0, filterTemplate: templateObj, rates: existingRates }]);

      setValue('translationTemplateBlocks.0.template_id', existingTemplateId || '');
      setValue('translationTemplateBlocks.0.filterTemplate', templateObj);
      setValue('translationTemplateBlocks.0.rates', existingRates);
      setValue('translationTemplateBlocks.0.rush_fee', item?.translation_rush_fee || 0);
      setValue('translation_template_id', existingTemplateId || '');
      setValue('selected_translation_rates', existingRates);
    }
  }, [item?.id, item?.translation_rates, setValue]);

  const filteredTranslationTemplateLoadOptions = async (inputValue, loadedOptions, { page }, blockIndex) => {
    const result = await dispatch(
      fetchTemplates(
        page,
        {
          pageLength: 20,
          sortBy: 'id',
          sortDirection: 'asc',
          search: inputValue,
          type: 'translation',
        },
        true,
        true
      )
    );
    const data = Array.isArray(result) ? result : result?.data || [];
    const hasMore = Array.isArray(result) ? false : page !== result?.last_page;

    const alreadySelectedIds = getSelectedTemplateIds(blockIndex);

    const filteredData = data.filter((template) => !alreadySelectedIds.includes(template.id));

    return {
      options: filteredData.map((template) => ({
        value: template.id,
        label: template.display_name || template.name || `Template ${template.id}`,
      })),
      hasMore,
      additional: {
        page: page + 1,
      },
    };
  };

  const handleTranslationTemplateSelected = async (selected, blockIndex) => {
    if (!selected) {
      clearTemplateSelection(blockIndex);
      return;
    }

    const alreadyUsed = getSelectedTemplateIds(blockIndex).includes(selected.value);
    if (alreadyUsed) {
      toast.error('This template is already selected in another block.');
      return;
    }

    const matchingTemplate = templates?.find((template) => template.id === selected.value);
    const directRates = matchingTemplate?.translation_rates || [];

    if (Array.isArray(directRates) && directRates.length > 0) {
      applyTemplateSelection(blockIndex, selected, directRates, matchingTemplate.rush_fee || 0);
      return;
    }

    setPendingBlockIndex(blockIndex);
    await dispatch(fetchSingleTemplate(selected.value, true));
  };

  useEffect(() => {
    if (pendingBlockIndex === null || pendingBlockIndex === undefined || !fetchedTemplate?.id) {
      return;
    }

    const currentBlock = translationTemplateBlocks[pendingBlockIndex];
    if (!currentBlock || currentBlock.filterTemplate?.value !== fetchedTemplate.id) {
      return;
    }

    applyTemplateSelection(pendingBlockIndex, currentBlock.filterTemplate, fetchedTemplate.translation_rates || [], fetchedTemplate.rush_fee || 0);
    setPendingBlockIndex(null);
  }, [fetchedTemplate, pendingBlockIndex, translationTemplateBlocks]);


  return (
    <>
      {translationTemplateBlocks.map((block, blockIndex) => (
        <div key={block.id} className="row-primary mt-3" style={{ flexDirection: 'column'}}>
          <div className="row-primary align-items-center">
            <h2 style={{width: '140px'}}>Select Translation Template</h2>

            <div style={{ width: '100%' }}>
              <Controller
                name={`translationTemplateBlocks.${blockIndex}.filterTemplate`}
                control={control}
                rules={{
                  required: "Please select translation template",
                }}
                render={({ field, fieldState: { error } }) => (
                  <>
                    <AsyncPaginate
                      styles={selectCustomStyles}
                      value={field.value}
                      loadOptions={(inputValue, loadedOptions, additional) =>
                        filteredTranslationTemplateLoadOptions(
                          inputValue,
                          loadedOptions,
                          additional,
                          blockIndex
                        )
                      }
                      cacheUniqs={[
                        translationTemplateBlocks
                          .map((b) => b.filterTemplate?.value)
                          .join(","),
                      ]}
                      isClearable
                      placeholder="Search translation templates"
                      additional={{ page: 1 }}
                      onChange={(selected) => {
                        field.onChange(selected);
                        handleTranslationTemplateSelected(selected, blockIndex);
                      }}
                    />

                    {error && (
                      <div style={{ marginTop: 10 }}>
                        <span className="error-message">{error.message}</span>
                      </div>
                    )}
                  </>
                )}
              />
            </div>
          </div>

          {block.filterTemplate && (
            <>
            <div className="row-primary mt-3">
              <h2>Rush Fee</h2>
              <div className="create inter_wrapper">
                <RateInput
                  control={control}
                  name={`translationTemplateBlocks.${blockIndex}.rush_fee`}
                  placeholder="Rush Fee"
                  currency_symbol={true}
                />
              </div>
            </div>

              {block.rates?.length > 0 ? (
                block.rates.map((rate, tierIndex) => (
                  <div key={`${block.id}-tier-${rate.tier_id || tierIndex}`} className="row-primary rate_container_outer">
                    <h2 className="fw-bold text-uppercase">{rate.tier_name || `Tier ${tierIndex + 1}`}</h2>

                    <div className="rate_container_wrapper w-100">
                      <div className="template_create rate_container">
                        <div className="row-primary align-items-end inner">
                          <h2 className="mb-3 my-0">Rates</h2>

                          <div className="Inter_rate_row">
                            <h4>Translation</h4>
                            <span className="static-unit-text">per word</span>
                            <div className="create inter_wrapper translation_rate_input">
                              <RateInput
                                control={control}
                                name={`translationTemplateBlocks.${blockIndex}.rates.${tierIndex}.translation_rate`}
                                placeholder="Translation rate"
                                currency_symbol={true}
                              />
                            </div>
                          </div>

                          <div className="Inter_rate_row">
                            <h4>Editing</h4>
                            <span className="static-unit-text">per word</span>
                            <div className="create inter_wrapper translation_rate_input">
                              <RateInput
                                control={control}
                                name={`translationTemplateBlocks.${blockIndex}.rates.${tierIndex}.editing_rate`}
                                placeholder="Editing rate"
                                currency_symbol={true}
                              />
                            </div>
                          </div>

                          <div className="Inter_rate_row">
                            <h4>DTP</h4>
                            <span className="static-unit-text">per work</span>
                            <div className="create inter_wrapper translation_rate_input">
                              <RateInput
                                control={control}
                                name={`translationTemplateBlocks.${blockIndex}.rates.${tierIndex}.dtp_rate`}
                                placeholder="DTP rate"
                                currency_symbol={true}
                              />
                            </div>
                          </div>

                        </div>

                        <div className="row-primary align-items-end inner">
                          <h2 className="mb-3 my-0">Rates</h2>

                          <div className="Inter_rate_row">
                            <h4>Review</h4>
                            <span className="static-unit-text">per page</span>
                            <div className="create inter_wrapper translation_rate_input">
                              <RateInput
                                control={control}
                                name={`translationTemplateBlocks.${blockIndex}.rates.${tierIndex}.review_rate`}
                                placeholder="Review rate"
                                currency_symbol={true}
                              />
                            </div>
                          </div>

                          <div className="Inter_rate_row">
                            <h4>Audio Recording</h4>
                            <span className="static-unit-text">per word</span>
                            <div className="create inter_wrapper translation_rate_input">
                              <RateInput
                                control={control}
                                name={`translationTemplateBlocks.${blockIndex}.rates.${tierIndex}.audio_recording_rate`}
                                placeholder="Audio Recording rate"
                                currency_symbol={true}
                              />
                            </div>
                          </div>

                          <div className="Inter_rate_row">
                            <h4>Transcription</h4>
                            <span className="static-unit-text">per audio minute</span>
                            <div className="create inter_wrapper translation_rate_input">
                              <RateInput
                                control={control}
                                name={`translationTemplateBlocks.${blockIndex}.rates.${tierIndex}.transcription_rate`}
                                placeholder="Transcription rate"
                                currency_symbol={true}
                              />
                            </div>
                          </div>

                        </div>

                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="row-primary mt-3">
                  <p style={{ color: '#6c7a89' }}>No template rates available for this template.</p>
                </div>
              )}
            </>
          )}
        </div>
      ))}
    </>
  );
};

export default TranslationTiersPreview;